import {
    ACTIVITY_SLOT_OPTIONS,
    CONTENT_TYPE_OPTIONS,
    RESET_CONTEXT_OPTIONS,
    SLOT_ELIGIBLE_STATE_KEYS,
    STATE_KEY_OPTIONS,
} from "../data";

function optionLabel(options, value) {
    return options.find((option) => option.value === value)?.label || value;
}

// Read-only, purely derived from the same Formik values/file state AddContent
// already holds — no new data source, no backend calls. `detailed` (used on
// the final Upload & Review step) adds the fields that don't fit the compact
// card shown on steps 1-3, so the admin has one place to check everything
// before hitting the existing Save action.
export default function PreviewCard({
    values,
    thumbnailPreviewUrl,
    contentFileName,
    teacherGuideFileName,
    thumbnailFileName,
    detailed = false,
    // EditContent's files aren't just "chosen or not" — an existing file can
    // be kept, replaced, or explicitly removed. When set, these override the
    // Add-only default text below instead of the plain file name.
    contentFileStatusLabel,
    teacherGuideStatusLabel,
    thumbnailStatusLabel,
}) {
    const typeLabel = optionLabel(CONTENT_TYPE_OPTIONS, values.contentType);
    const stateLabel = optionLabel(STATE_KEY_OPTIONS, values.stateKey);
    const contextLabels = values.contexts.map((value) => optionLabel(RESET_CONTEXT_OPTIONS, value));
    const isSlotEligible = SLOT_ELIGIBLE_STATE_KEYS.includes(values.stateKey);
    const slotLabel = isSlotEligible && values.activitySlot !== "" ? optionLabel(ACTIVITY_SLOT_OPTIONS, Number(values.activitySlot)) : null;

    return (
        <div className="wizard-preview-card">
            <div className="wizard-preview-label">Preview</div>
            <div className="wizard-preview-help">This is how your content may appear to teachers.</div>

            <div className="wizard-preview-thumb">
                {thumbnailPreviewUrl ? (
                    <img src={thumbnailPreviewUrl} alt="Thumbnail preview" />
                ) : (
                    <div className="wizard-preview-thumb-placeholder" />
                )}
            </div>

            <div className="wizard-preview-title">{values.title?.trim() || "Untitled activity"}</div>

            <div className="d-flex flex-wrap gap-2 mt-2">
                <span className={`content-type-badge content-type-${values.contentType.toLowerCase()}`}>{typeLabel}</span>
                <span className="wizard-preview-state-badge">{stateLabel}</span>
            </div>

            {(values.description?.trim() || values.goal?.trim()) && (
                <div className="wizard-preview-desc">{values.description?.trim() || values.goal?.trim()}</div>
            )}

            {contextLabels.length > 0 && (
                <div className="wizard-preview-contexts">
                    <span className="wizard-preview-contexts-label">Best for: </span>
                    {contextLabels.join(", ")}
                </div>
            )}

            {detailed && (
                <div className="wizard-preview-detail-list">
                    {values.author?.trim() && (
                        <div>
                            <strong>Author:</strong> {values.author.trim()}
                        </div>
                    )}
                    {slotLabel && (
                        <div>
                            <strong>Replaces:</strong> {slotLabel}
                        </div>
                    )}
                    {values.teacherInstructions?.trim() && (
                        <div>
                            <strong>Teacher instructions:</strong> {values.teacherInstructions.trim()}
                        </div>
                    )}
                    <div>
                        <strong>Content file:</strong> {contentFileStatusLabel ?? (contentFileName || "Not chosen yet")}
                    </div>
                    <div>
                        <strong>Teacher preview video:</strong> {teacherGuideStatusLabel ?? (teacherGuideFileName || "Not added")}
                    </div>
                    <div>
                        <strong>Thumbnail:</strong> {thumbnailStatusLabel ?? (thumbnailFileName || "Auto-generated from video")}
                    </div>
                </div>
            )}
        </div>
    );
}
